import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { CalendarDays, Check, ChevronDown, Search, X } from 'lucide-react';
import { openNativeDatePickerSafely, preventManualDateEntry, preventManualDatePasteOrDrop } from '../../../utils/date';
import { useLanguage } from '../../../features/language/useLanguage';
import { cn } from '../../../utils/cn';

interface PurchaseOrderFiltersProps {
  searchFilter: string;
  onSearchFilterChange: (value: string) => void;
  statusFilter: string;
  onStatusFilterChange: (value: string) => void;
  orderStartDateFilter: string;
  onOrderStartDateFilterChange: (value: string) => void;
  orderEndDateFilter: string;
  onOrderEndDateFilterChange: (value: string) => void;
  deliveryStartDateFilter: string;
  onDeliveryStartDateFilterChange: (value: string) => void;
  deliveryEndDateFilter: string;
  onDeliveryEndDateFilterChange: (value: string) => void;
  paymentStatusFilter: string;
  onPaymentStatusFilterChange: (value: string) => void;
  deliveryStatusFilter: string;
  onDeliveryStatusFilterChange: (value: string) => void;
}

interface SelectConfig {
  label: string;
  value: string;
  onChange: (next: string) => void;
  options: string[];
  testId: string;
}

export const PurchaseOrderFilters: React.FC<PurchaseOrderFiltersProps> = ({
  searchFilter,
  onSearchFilterChange,
  statusFilter,
  onStatusFilterChange,
  orderStartDateFilter,
  onOrderStartDateFilterChange,
  orderEndDateFilter,
  onOrderEndDateFilterChange,
  deliveryStartDateFilter,
  onDeliveryStartDateFilterChange,
  deliveryEndDateFilter,
  onDeliveryEndDateFilterChange,
  paymentStatusFilter,
  onPaymentStatusFilterChange,
  deliveryStatusFilter,
  onDeliveryStatusFilterChange,
}) => {
  const { t } = useLanguage();
  const [isDatePanelOpen, setIsDatePanelOpen] = useState(false);
  const [draftOrderStartDate, setDraftOrderStartDate] = useState(orderStartDateFilter);
  const [draftOrderEndDate, setDraftOrderEndDate] = useState(orderEndDateFilter);
  const [draftDeliveryStartDate, setDraftDeliveryStartDate] = useState(deliveryStartDateFilter);
  const [draftDeliveryEndDate, setDraftDeliveryEndDate] = useState(deliveryEndDateFilter);

  const rootRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [panelAlign, setPanelAlign] = useState<'left' | 'right'>('right');
  const [panelWidth, setPanelWidth] = useState(560);
  const [panelVisible, setPanelVisible] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setPanelVisible(false);
        setIsDatePanelOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // The panel anchors to the trigger button and stays inside the filters card,
  // which is always within the content area — so it never spills behind the
  // sidebar or off-screen. It grows whichever direction has more room, and
  // shrinks to fit when neither side can hold the preferred width.
  const updatePanelLayout = useCallback(() => {
    const button = triggerRef.current;
    const root = rootRef.current;
    if (!button || !root) return;
    const btn = button.getBoundingClientRect();
    const bounds = root.getBoundingClientRect();
    const margin = 8;
    const preferredWidth = 560;
    const spaceExpandingRight = bounds.right - btn.left;
    const spaceExpandingLeft = btn.right - bounds.left;
    const expandRight = spaceExpandingRight >= spaceExpandingLeft;
    const available = (expandRight ? spaceExpandingRight : spaceExpandingLeft) - margin;
    setPanelAlign(expandRight ? 'left' : 'right');
    setPanelWidth(Math.round(Math.max(288, Math.min(preferredWidth, available))));
  }, []);

  useLayoutEffect(() => {
    if (!isDatePanelOpen) return;
    const frame = requestAnimationFrame(() => {
      updatePanelLayout();
      setPanelVisible(true);
    });
    window.addEventListener('resize', updatePanelLayout);
    window.addEventListener('scroll', updatePanelLayout, true);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', updatePanelLayout);
      window.removeEventListener('scroll', updatePanelLayout, true);
    };
  }, [isDatePanelOpen, updatePanelLayout]);

  const handleApplyDateFilter = () => {
    onOrderStartDateFilterChange(draftOrderStartDate);
    onOrderEndDateFilterChange(draftOrderEndDate);
    onDeliveryStartDateFilterChange(draftDeliveryStartDate);
    onDeliveryEndDateFilterChange(draftDeliveryEndDate);
    setPanelVisible(false);
    setIsDatePanelOpen(false);
  };

  const hasDateFilter =
    Boolean(orderStartDateFilter) ||
    Boolean(orderEndDateFilter) ||
    Boolean(deliveryStartDateFilter) ||
    Boolean(deliveryEndDateFilter);

  const selects: SelectConfig[] = [
    {
      label: t('purchaseOrder.filters.order'),
      value: statusFilter,
      onChange: onStatusFilterChange,
      options: ['all', 'draft', 'ordered', 'cancelled'],
      testId: 'po-status-filter',
    },
    {
      label: t('purchaseOrder.filters.payment'),
      value: paymentStatusFilter,
      onChange: onPaymentStatusFilterChange,
      options: ['all', 'unpaid', 'paid', 'refunded'],
      testId: 'po-payment-status-filter',
    },
    {
      label: t('purchaseOrder.filters.delivery'),
      value: deliveryStatusFilter,
      onChange: onDeliveryStatusFilterChange,
      options: ['all', 'pending', 'shipping', 'received'],
      testId: 'po-delivery-status-filter',
    },
  ];

  const optionLabel = (opt: string) =>
    t(`purchaseOrder.filters.options.${opt}`, undefined, opt.charAt(0).toUpperCase() + opt.slice(1));

  const dateInputClass =
    'block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2';

  return (
    <div className="mb-4 relative" data-test-id="po-filters" ref={rootRef}>
      <div className="flex flex-col md:flex-row flex-wrap gap-4">
        <div className="flex-1 min-w-[260px]">
          <label htmlFor="poSearchFilter" className="block text-sm font-medium text-gray-700">
            {t('purchaseOrder.filters.search')}
          </label>
          <div className="relative mt-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <input
              id="poSearchFilter"
              type="text"
              placeholder={t('purchaseOrder.filters.searchPlaceholder')}
              value={searchFilter}
              onChange={(e) => onSearchFilterChange(e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border pl-9 pr-3 py-2"
              data-test-id="po-search-input"
            />
          </div>
        </div>

        {selects.map((col) => (
          <div key={col.label} className="w-full md:w-44">
            <label className="block text-sm font-medium text-gray-700">{col.label}</label>
            <Listbox value={col.value} onChange={col.onChange}>
              <div className="relative mt-1">
                <ListboxButton
                  data-test-id={col.testId}
                  className="relative w-full cursor-default rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-left shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:text-sm h-[38px]"
                >
                  <span className="block truncate capitalize">{optionLabel(col.value)}</span>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronDown className="h-4 w-4 text-gray-400" aria-hidden="true" />
                  </span>
                </ListboxButton>
                <ListboxOptions className="absolute z-30 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                  {col.options.map((opt) => (
                    <ListboxOption
                      key={opt}
                      value={opt}
                      className={({ focus }) =>
                        `relative cursor-default select-none py-2 pl-10 pr-4 ${focus ? 'bg-primary-100 text-primary-900' : 'text-gray-900'}`
                      }
                    >
                      {({ selected }) => (
                        <>
                          <span className={`block truncate capitalize ${selected ? 'font-medium' : 'font-normal'}`}>
                            {optionLabel(opt)}
                          </span>
                          {selected ? (
                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-primary-600">
                              <Check className="h-4 w-4" aria-hidden="true" />
                            </span>
                          ) : null}
                        </>
                      )}
                    </ListboxOption>
                  ))}
                </ListboxOptions>
              </div>
            </Listbox>
          </div>
        ))}

        <div className="relative" ref={popoverRef}>
          <label className="block text-sm font-medium text-gray-700">{t('common.dateRange.label')}</label>
          <button
            type="button"
            ref={triggerRef}
            onClick={() => {
              setPanelVisible(false);
              setIsDatePanelOpen((v) => !v);
            }}
            aria-expanded={isDatePanelOpen}
            className={cn(
              'mt-1 inline-flex items-center gap-2 h-[38px] px-3 rounded-md border shadow-sm text-sm font-medium transition-colors focus:outline-none focus:ring-1 focus:ring-primary-500',
              isDatePanelOpen
                ? 'bg-primary-50 border-primary-400 text-primary-700 ring-1 ring-inset ring-primary-600/20'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            )}
            data-test-id="po-date-range-toggle"
          >
            <CalendarDays className="h-4 w-4" />
            <span>{t('purchaseOrder.filters.dates')}</span>
            {hasDateFilter && (
              <span className="inline-flex h-1.5 w-1.5 rounded-full bg-primary-500" aria-hidden="true" />
            )}
          </button>

          {isDatePanelOpen && (
            <div
              style={{ width: panelWidth }}
              className={cn(
                'absolute z-40 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 p-4',
                'transition duration-150 ease-out',
                panelAlign === 'right' ? 'right-0 origin-top-right' : 'left-0 origin-top-left',
                panelVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
              )}
            >
              <div className="flex justify-between items-center mb-4 pb-3 border-b border-gray-100">
                <h3 className="text-sm font-semibold text-gray-900">{t('purchaseOrder.filters.dateRangeFilters')}</h3>
                <button
                  type="button"
                  onClick={() => {
                    setPanelVisible(false);
                    setIsDatePanelOpen(false);
                  }}
                  className="text-gray-400 hover:text-gray-600 p-1 rounded-md hover:bg-gray-100"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className={cn('grid gap-3', panelWidth >= 480 ? 'grid-cols-2' : 'grid-cols-1')}>
                <div className="space-y-2 bg-gray-50 p-3 rounded-md ring-1 ring-inset ring-gray-200">
                  <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    {t('purchaseOrder.filters.orderDate')}
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[11px] font-medium text-gray-500 block mb-1">
                        {t('common.dateRange.startDate')}
                      </label>
                      <input
                        type="date"
                        value={draftOrderStartDate}
                        onChange={(e) => setDraftOrderStartDate(e.target.value)}
                        onClick={openNativeDatePickerSafely}
                        onKeyDown={preventManualDateEntry}
                        onPaste={preventManualDatePasteOrDrop}
                        onDrop={preventManualDatePasteOrDrop}
                        className={dateInputClass}
                      />
                    </div>
                    <div>
                      <label className="text-[11px] font-medium text-gray-500 block mb-1">
                        {t('common.dateRange.endDate')}
                      </label>
                      <input
                        type="date"
                        value={draftOrderEndDate}
                        onChange={(e) => setDraftOrderEndDate(e.target.value)}
                        onClick={openNativeDatePickerSafely}
                        onKeyDown={preventManualDateEntry}
                        onPaste={preventManualDatePasteOrDrop}
                        onDrop={preventManualDatePasteOrDrop}
                        className={dateInputClass}
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2 bg-gray-50 p-3 rounded-md ring-1 ring-inset ring-gray-200">
                  <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    {t('purchaseOrder.filters.deliveryDate')}
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[11px] font-medium text-gray-500 block mb-1">
                        {t('common.dateRange.startDate')}
                      </label>
                      <input
                        type="date"
                        value={draftDeliveryStartDate}
                        onChange={(e) => setDraftDeliveryStartDate(e.target.value)}
                        onClick={openNativeDatePickerSafely}
                        onKeyDown={preventManualDateEntry}
                        onPaste={preventManualDatePasteOrDrop}
                        onDrop={preventManualDatePasteOrDrop}
                        className={dateInputClass}
                      />
                    </div>
                    <div>
                      <label className="text-[11px] font-medium text-gray-500 block mb-1">
                        {t('common.dateRange.endDate')}
                      </label>
                      <input
                        type="date"
                        value={draftDeliveryEndDate}
                        onChange={(e) => setDraftDeliveryEndDate(e.target.value)}
                        onClick={openNativeDatePickerSafely}
                        onKeyDown={preventManualDateEntry}
                        onPaste={preventManualDatePasteOrDrop}
                        onDrop={preventManualDatePasteOrDrop}
                        className={dateInputClass}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => {
                    setPanelVisible(false);
                    setIsDatePanelOpen(false);
                  }}
                  className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                >
                  {t('auth.common.cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleApplyDateFilter}
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-semibold bg-primary-600 text-white rounded-md hover:bg-primary-700 shadow-sm ring-1 ring-inset ring-primary-700/20 transition-colors"
                >
                  {t('purchaseOrder.filters.applyFilters')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
